import { useCallback, useEffect, useState } from 'react';
import type { ConsentMode } from '@archipro-website/localisation';
import {
    hasConsentNoticeCookie,
    writeConsentNoticeCookie,
} from './consent-cookie';

interface UseConsentNoticeArgs {
    consentMode: ConsentMode;
    topDomain: string | undefined;
}

/**
 * Owns the "should the US-style notice card be visible right now?" decision.
 * It folds three signals together (consent mode, hydration, acknowledgement
 * cookie) so callers only see a single boolean plus a dismiss handler.
 *
 * `topDomain` is optional. When set, dismissal writes a cross-subdomain
 * cookie scoped to that domain. When unset (local dev, preview hosts) the
 * cookie falls back to host-scoped so the banner still dismisses; production
 * misconfiguration is surfaced via a separate warning in the banner.
 */
export const useConsentNotice = ({
    consentMode,
    topDomain,
}: UseConsentNoticeArgs) => {
    const [hydrated, setHydrated] = useState(false);
    const [isDismissed, setIsDismissed] = useState(true);

    useEffect(() => {
        setHydrated(true);
        setIsDismissed(hasConsentNoticeCookie());
    }, []);

    const dismiss = useCallback(() => {
        writeConsentNoticeCookie(topDomain);
        setIsDismissed(true);
    }, [topDomain]);

    const shouldShow = consentMode === 'notice' && hydrated && !isDismissed;

    return { shouldShow, dismiss };
};
